import React, { useEffect, useState } from 'react';
import { Link } from '../lib/routerCompat';
import { useToast } from '../ToastContext';
import DemoBadge from '../components/DemoBadge';
import { db } from '../services/db';
import { isCreatorProfileComplete } from '../lib/creatorProfile';
import { MAX_PITCH_CHARS, countWords, validatePitch } from '../lib/pitch';
import { STAT_PLACEHOLDER } from '../lib/stats';
import type { Application, ApplicationStatus, CreatorRow } from '../types';

/** How each application status reads on the creator's own dashboard. */
const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: 'Waiting on the brand',
  accepted: 'Accepted',
  declined: 'Not selected',
  withdrawn: 'Withdrawn',
};

/** Reuses the escrow badge palette so status colour means one thing app-wide. */
const APPLICATION_BADGE: Record<ApplicationStatus, string> = {
  submitted: 'badge-pending',
  accepted: 'badge-released',
  declined: 'badge-pending',
  withdrawn: 'badge-pending',
};

export default function CreatorView(): JSX.Element {
  const showToast = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [withdrawingId, setWithdrawingId] = useState<number | null>(null);
  const [creator, setCreator] = useState<CreatorRow | null>(null);
  const [creatorResolved, setCreatorResolved] = useState<boolean>(false);
  const [pitch, setPitch] = useState<string>('');
  const [pitchDraft, setPitchDraft] = useState<string>('');
  const [editingPitch, setEditingPitch] = useState<boolean>(false);
  const [savingPitch, setSavingPitch] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Resolve whose dashboard this is. Signed in with no creator row yet is an
  // expected state (onboarding incomplete), so a null result renders a setup
  // notice rather than crashing; demo mode gets the bundled showcase profile.
  useEffect(() => {
    let cancelled = false;
    db.getMyCreator()
      .then((row) => {
        if (cancelled) return;
        setCreator(row);
        // Demo mode has no backend to read the pitch back from, so the bundled
        // row is the only source for it. The draft is seeded when the editor
        // opens, so a late load can't overwrite what the creator is typing.
        if (db.demoMode && row?.pitch) setPitch(row.pitch);
      })
      .catch(() => {
        // Resolution failure reads the same as "no profile yet": the dashboard
        // renders its setup notice instead of a broken shell.
      })
      .finally(() => {
        if (!cancelled) setCreatorResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // What this creator has pitched for. Failures leave the list empty rather
  // than breaking the dashboard around it.
  useEffect(() => {
    let cancelled = false;
    db.getMyApplications()
      .then((rows) => {
        if (!cancelled) setApplications(rows);
      })
      .catch(() => {
        // The rest of the dashboard is unaffected.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const creatorId = creator?.id ?? null;
  const avatarPath = creator?.avatar_path ?? null;

  // Mint a signed URL for the uploaded avatar. The bucket is private, so this
  // is per-render rather than a stored URL; failing quietly leaves the emoji.
  useEffect(() => {
    if (!avatarPath) {
      setAvatarUrl(null);
      return undefined;
    }
    let cancelled = false;
    db.getMediaUrls([avatarPath])
      .then((urls) => {
        if (!cancelled) setAvatarUrl(urls[avatarPath] ?? null);
      })
      .catch(() => {
        // No signed URL: the emoji placeholder renders instead.
      });
    return () => {
      cancelled = true;
    };
  }, [avatarPath]);
  // Only a profile carrying everything onboarding collects is worth showing to
  // brands, so completeness gates the publish control.
  const profileComplete = isCreatorProfileComplete(creator);

  // Load the saved pitch once the creator is known so it survives a page
  // refresh. Demo mode has no backend, so we keep what's already in state.
  useEffect(() => {
    if (db.demoMode || creatorId === null) return undefined;
    let cancelled = false;
    db.getCreatorPitch(creatorId)
      .then((saved) => {
        if (cancelled || saved == null) return;
        // Only the displayed pitch — startEditingPitch seeds the draft from it,
        // so a fetch landing mid-edit can't wipe an in-progress draft.
        setPitch(saved);
      })
      .catch(() => {
        // Read failure: leave whatever is already in state.
      });
    return () => {
      cancelled = true;
    };
  }, [creatorId]);

  const trimmedDraft = pitchDraft.trim();
  // Shared with the per-campaign apply composer (PIT-78) so the two cannot
  // disagree about what a valid pitch is.
  const pitchError = validatePitch(pitchDraft);

  const startEditingPitch = (): void => {
    setPitchDraft(pitch);
    setEditingPitch(true);
  };

  const cancelEditingPitch = (): void => {
    setPitchDraft(pitch);
    setEditingPitch(false);
  };

  const savePitch = async (): Promise<void> => {
    if (pitchError || savingPitch || creatorId === null) return;
    setSavingPitch(true);
    try {
      await db.updateCreatorPitch(creatorId, trimmedDraft);
      setPitch(trimmedDraft);
      setEditingPitch(false);
      showToast('Pitch saved');
    } catch {
      showToast('Could not save pitch — please try again');
    } finally {
      setSavingPitch(false);
    }
  };

  // Publishing is the creator's own visibility switch (PIT-63): a profile stays
  // owner-only until they say otherwise, and can be pulled back out of Discover
  // at any time. RLS enforces that this only ever touches their own row.
  const togglePublished = async (): Promise<void> => {
    if (!creator || publishing) return;
    const next = !creator.published;
    // Publishing an unfinished profile would put a half-built card in front of
    // brands; the control renders as a link to finish it instead.
    if (next && !profileComplete) return;
    setPublishing(true);
    try {
      await db.updateMyCreator({ published: next });
      setCreator((prev) => (prev ? { ...prev, published: next } : prev));
      showToast(
        db.demoMode
          ? 'Demo mode — visibility not saved'
          : next
            ? 'Profile published — brands can find you'
            : 'Profile unpublished — hidden from Discover'
      );
    } catch {
      showToast('Could not update visibility — please try again');
    } finally {
      setPublishing(false);
    }
  };

  // Withdrawing is the only move a creator has on their own application:
  // accepting and declining belong to the brand, and the RLS update policies
  // say so. A refused write returns no row rather than erroring.
  const withdrawApplication = async (id: number): Promise<void> => {
    if (withdrawingId !== null) return;
    setWithdrawingId(id);
    try {
      const updated = await db.setApplicationStatus(id, 'withdrawn');
      if (!updated && !db.demoMode) {
        showToast('That application can no longer be withdrawn — reload to see it');
        return;
      }
      setApplications((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: 'withdrawn' as const } : a))
      );
      showToast('Application withdrawn');
    } catch {
      showToast('Could not withdraw that application — please try again');
    } finally {
      setWithdrawingId(null);
    }
  };

  return (
    <div className="view">
      {/* No page-level badge: since PIT-47 the profile below is genuinely the
          signed-in creator's, and claiming the whole page is demo data would
          understate that. What is still invented is marked where it sits.
          In demo mode there is no session, so the profile itself is a bundled
          showcase and says so. */}
      {db.demoMode && <DemoBadge label="Demo mode — sample profile" />}
      {/* Profile — the signed-in user's own creator row (bundled showcase in
          demo mode), or a setup notice when they haven't built one yet. */}
      {creator ? (
        <div
          className="panel"
          style={{ display: 'flex', alignItems: 'center', gap: 28, marginBottom: 24 }}
        >
          <div
            className="creator-thumb"
            style={{
              width: 80,
              height: 80,
              minWidth: 80,
              background: creator.bg || '#1a1208',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {/* Uploaded avatar when there is one, else the emoji placeholder
                every seeded row still uses (PIT-71). */}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={creator.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: 54 }}>{creator.emoji}</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div className="creator-name" style={{ fontSize: 27, marginBottom: 4 }}>
              {creator.name}
            </div>
            <div className="creator-handle" style={{ marginBottom: 10 }}>
              {creator.handle}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {creator.niche && <span className="niche-tag primary">{creator.niche}</span>}
              {(creator.tags ?? []).map((t) => (
                <span className="niche-tag" key={t}>
                  {t}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Link to="/creator/edit" className="btn-outline">
                Edit profile
              </Link>
              {/* Where a creator goes to find work. The nav has no creator-side
                  entry for it yet — see PIT-76 — so the dashboard carries it. */}
              <Link to="/opportunities" className="btn-outline">
                Browse campaigns
              </Link>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 32, textAlign: 'center' }}>
            {[
              { label: 'Reach', value: creator.reach || STAT_PLACEHOLDER },
              { label: 'Engagement', value: creator.engagement || STAT_PLACEHOLDER },
              { label: 'Deals Done', value: creator.completed ?? 0 },
            ].map((s) => (
              <div key={s.label}>
                <div
                  style={{
                    fontFamily: 'var(--cinzel)',
                    fontSize: 27,
                    fontWeight: 700,
                    color: 'var(--gold-light)',
                    marginBottom: 4,
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    fontFamily: 'var(--cinzel)',
                    letterSpacing: 2,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="panel" style={{ marginBottom: 24 }}>
          <div className="creator-name" style={{ fontSize: 27, marginBottom: 6 }}>
            {creatorResolved ? 'No creator profile yet' : 'Loading your profile…'}
          </div>
          {creatorResolved && (
            <>
              <div className="section-sub" style={{ marginBottom: 14 }}>
                This account isn&apos;t linked to a creator profile. Once you build one, your
                details and pitch will live here.
              </div>
              <Link to="/onboarding" className="btn-outline">
                Build your profile
              </Link>
            </>
          )}
        </div>
      )}

      {/* Visibility — the creator's own control over appearing in Discover. */}
      {creator && (
        <div
          className="panel"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
            marginBottom: 24,
          }}
        >
          <div style={{ flex: 1, minWidth: 260 }}>
            <div
              style={{
                fontSize: 13.5,
                fontFamily: 'var(--cinzel)',
                letterSpacing: 2,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Profile Status
            </div>
            <div
              style={{
                fontFamily: 'var(--cinzel)',
                fontSize: 22.5,
                fontWeight: 700,
                color: creator.published ? 'var(--gold-light)' : 'var(--text-muted)',
                marginBottom: 4,
              }}
            >
              {creator.published ? 'Published' : 'Draft'}
            </div>
            <div className="section-sub">
              {creator.published
                ? 'Brands can find you in Discover.'
                : profileComplete
                  ? 'Only you can see this profile. Publish it to start appearing in Discover.'
                  : 'Only you can see this profile. Finish your details before publishing it.'}
            </div>
          </div>
          {creator.published ? (
            <button className="btn-outline" onClick={togglePublished} disabled={publishing}>
              {publishing ? 'Saving…' : 'Unpublish'}
            </button>
          ) : profileComplete ? (
            <button
              className="btn-submit-gold"
              style={{ width: 'auto', padding: '12px 28px' }}
              onClick={togglePublished}
              disabled={publishing}
            >
              {publishing ? 'Saving…' : 'Publish Profile'}
            </button>
          ) : (
            <Link to="/onboarding" className="btn-outline">
              Finish your profile
            </Link>
          )}
        </div>
      )}

      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>

      {/* My Pitch */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
          marginBottom: 6,
        }}
      >
        <div className="section-header">My Pitch</div>
        {/* Without a creator row there is nothing to attach a pitch to, so the
            editor stays closed until onboarding has created one. */}
        {!editingPitch && creator && (
          <button className="btn-outline" onClick={startEditingPitch}>
            {pitch ? 'Edit Pitch' : '+ Add Pitch'}
          </button>
        )}
      </div>
      <div className="section-sub" style={{ marginBottom: 16 }}>
        Your personal pitch to brands — who you are, what you stand for, and why you&apos;re the
        right creator. Keep it conversational and authentic, not corporate.
      </div>

      {editingPitch ? (
        <div className="panel my-pitch-editor">
          <div className="form-section-label" style={{ marginBottom: 10 }}>
            Write your pitch
          </div>
          <ul className="my-pitch-tips">
            <li>Speak in your own voice — write like you talk.</li>
            <li>Lead with what makes working with you different.</li>
            <li>Aim for 250–500 words. Line breaks help readability.</li>
          </ul>
          <textarea
            className={`form-input${pitchError && trimmedDraft.length > 0 ? ' input-error' : ''}`}
            style={{ resize: 'vertical', minHeight: 160 }}
            placeholder="Hey, I'm… Here's why brands love working with me…"
            aria-label="My pitch"
            value={pitchDraft}
            maxLength={MAX_PITCH_CHARS}
            onChange={(e) => setPitchDraft(e.target.value)}
          />
          <div className="my-pitch-meta">
            <span className="my-pitch-count">{countWords(pitchDraft)} words</span>
            <span className="my-pitch-count">
              {pitchDraft.length} / {MAX_PITCH_CHARS}
            </span>
          </div>
          {pitchError && <div className="my-pitch-error">{pitchError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn-submit-gold"
              onClick={savePitch}
              disabled={Boolean(pitchError) || savingPitch}
            >
              {savingPitch ? 'Saving…' : 'Save Pitch'}
            </button>
            <button className="btn-outline" onClick={cancelEditingPitch}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="panel my-pitch-display">
          {pitch ? (
            <p className="my-pitch-text">{pitch}</p>
          ) : creator ? (
            <p className="my-pitch-empty">
              You haven&apos;t written a pitch yet. Add one to help brands understand who you are
              beyond the stats.
            </p>
          ) : (
            <p className="my-pitch-empty">
              Build your creator profile first — your pitch is saved against it.
            </p>
          )}
        </div>
      )}

      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>

      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>

      {/* My Applications — real work the creator has pitched for (PIT-82).
          This replaced a hardcoded grid of three invented briefs and an
          earnings panel claiming $13,200 earned. Both were marked "Sample
          data" by PIT-72 because nothing backed them; with real applications
          on the same screen, marked fiction beside fact is worse than either
          alone, so they are gone rather than badged. */}
      <div className="section-header" style={{ marginTop: 24, marginBottom: 6 }}>
        My Applications
      </div>
      <div className="section-sub" style={{ marginBottom: 20 }}>
        Campaigns you have pitched for, and where each one stands.
      </div>

      {applications.length === 0 ? (
        <div className="panel">
          <div className="creator-name" style={{ fontSize: 22.5, marginBottom: 8 }}>
            No applications yet
          </div>
          <div className="section-sub" style={{ marginBottom: 14 }}>
            Open campaigns from brands are listed on the campaign board. Applying takes a pitch and
            a minute.
          </div>
          <Link to="/opportunities" className="btn-outline">
            Browse campaigns
          </Link>
        </div>
      ) : (
        <div className="payment-grid">
          {applications.map((a) => (
            <div className="payment-card" key={a.id}>
              <div className="payment-card-title">
                {/* A campaign the policy cannot show is still an application
                    worth listing — see PIT-82's migration. */}
                {a.campaign?.title ?? 'Campaign unavailable'}
              </div>
              {a.campaign?.budget && <div className="payment-amount">{a.campaign.budget}</div>}

              <p className="my-pitch-text" style={{ margin: '12px 0' }}>
                {a.pitch_text}
              </p>

              <div
                className={`escrow-badge ${APPLICATION_BADGE[a.status]}`}
                role="status"
                style={{ marginBottom: 10 }}
              >
                {APPLICATION_STATUS_LABEL[a.status]}
              </div>

              {a.status === 'submitted' && (
                <div>
                  <button
                    className="btn-outline"
                    onClick={() => withdrawApplication(a.id)}
                    disabled={withdrawingId !== null}
                  >
                    {withdrawingId === a.id ? 'Withdrawing…' : 'Withdraw'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
